import React, { memo, useCallback, useMemo } from 'react';
import { CalendarOutlined } from '@ant-design/icons';
import { Button, Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import CustomTag from '@components/CustomTag';

interface BannerDisplayTimeColProps {
  startDate: string;
  expirationDate: string | null;
  bannerStatus: string;
}

const BannerDisplayTimeCol = ({
  startDate,
  expirationDate,
  bannerStatus,
}: BannerDisplayTimeColProps) => {
  const { t } = useTranslation('banners');

  const tagColor = useMemo(() => {
    if (bannerStatus === 'WAITING') {
      return 'blue';
    }
    if (bannerStatus === 'EXPIRED') {
      return 'orange';
    }
    return 'green';
  }, [bannerStatus]);

  const tagStyled = useCallback(
    (dateValue?: string) => {
      const format = 'DD/MM/YYYY, HH:mm';
      return (
        dateValue && (
          <CustomTag type="solid" color={tagColor} style={{ margin: 0 }}>
            {moment(dateValue)?.format(format)}
          </CustomTag>
        )
      );
    },
    [tagColor],
  );

  const displayDate = useMemo(() => {
    let type = 'until';
    if (!expirationDate) {
      const isActive = bannerStatus === 'ACTIVE';
      type = isActive ? 'since' : 'from';
    }
    return (
      <>
        {expirationDate && tagStyled(startDate)}
        <span style={{ margin: '0 6px' }}>
          {t(`columns.displayTime.${type}`)}
        </span>
        {tagStyled(expirationDate ?? startDate)}
      </>
    );
  }, [bannerStatus, expirationDate, startDate, t, tagStyled]);

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      <CustomTag color={tagColor}>
        {t(`columns.displayTime.status.${bannerStatus}`)}
      </CustomTag>
      <Popover content={displayDate}>
        <Button type="link" icon={<CalendarOutlined />} />
      </Popover>
    </div>
  );
};

export default memo(BannerDisplayTimeCol);
